<template>
  <div>
    <v-card class="elevation-0">
      <v-card-title class="text-subtitle-1">
        <div>
          <h3>{{ `${$t('Receipt')} #${receipt.nr}` }}</h3>
          <span class="mt-2">
            <v-icon
              small
              class="mr-1 mt-n1"
            >fal fa-calendar-alt</v-icon>
            {{ receipt.completed | moment("D MMMM YYYY H:mm") }}
          </span>
          <div class="mt-1">
            <v-icon
              small
              class="mr-1 mt-n1"
            >far fa-user-alt</v-icon>
            {{ $t('Processed by {0}', [receipt.creator.fullName]) }}
          </div>
        </div>
        <v-spacer />
        <div class="text-right">
          <status-chip
            :status-code="receipt.status"
            status-type="receipt"
            button-size="default"
          />
        </div>
      </v-card-title>
      <v-divider />

      <v-card-text class="pa-0">
        <!-- customer information -->
        <div class="pa-2">
          <v-simple-table
            v-if="receipt.customer"
            class="v-data-table--noborders v-data-table--nohover"
            dense
          >
            <tbody>
              <tr>
                <td class="font-weight-bold">
                  <v-icon
                    small
                    class="mr-2"
                  >far fa-fw fa-user</v-icon>
                  {{ $t('Customer') }}
                </td>
                <td class="text-right">{{ receipt.customer.fullName }}</td>
              </tr>
              <tr v-if="receipt.customer.email">
                <td class="font-weight-bold">
                  <v-icon
                    class="mr-2"
                    small
                  >far fa-fw fa-envelope</v-icon>
                  {{ $t('Email') }}
                </td>
                <td class="text-right"><a :href="`mailto:${receipt.customer.email}`">{{ receipt.customer.email }}</a></td>
              </tr>
              <tr v-if="receipt.customer.phone">
                <td class="font-weight-bold">
                  <v-icon
                    class="mr-2"
                    small
                  >far fa-fw fa-phone</v-icon>
                  {{ $t('Phone') }}
                </td>
                <td class="text-right">{{ receipt.customer.phone }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <v-simple-table
            v-else
            class="v-data-table--noborders v-data-table--nohover"
            dense
          >
            <tbody>
              <tr>
                <td
                  class="font-weight-bold"
                  width="75%"
                >
                  <v-icon
                    class="mr-2"
                    small
                  >far fa-fw fa-user</v-icon>
                  {{ $t('Customer') }}
                </td>
                <td class="text-right">{{ $t('Guest') }}</td>
                <td
                  class="text-right"
                  style="padding-right: 0"
                >
                  <v-btn
                    class="white--text"
                    color="blue-grey"
                    ripple
                    small
                    @click="showCustomersDialog = true"
                  >
                    <v-icon
                      class="mr-2"
                      small
                    >fal fa-search</v-icon>
                    {{ $t('Search {0}', [$t('Customer')]) }}
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>
        <v-divider />

        <!-- receipt summary -->
        <div
          v-if="receipt.receiptProducts"
          class="pa-2"
        >
          <v-simple-table class="table-vs">
            <thead>
              <tr>
                <th>{{ $t('Barcode').toUpperCase() }}</th>
                <th>{{ $t('Product').toUpperCase() }}</th>
                <th>{{ $t('Quantity').toUpperCase() }}</th>
                <th style="min-width: 100px">{{ $t('Total').toUpperCase() }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in receipt.receiptProducts"
                :key="item.id"
              >
                <td>{{ _.get(item, 'barcode') || `&lt;${$t('None')}&gt;` }}</td>
                <td>{{ item.display }}</td>
                <td>{{ `${item.qty}x` }}</td>
                <td>{{ item.totalInclVat | money }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>

        <v-col
          sm="6"
          offset-sm="6"
        >
          <p
            v-if="receipt.note"
            class="pl-4"
          >{{ `${$t('Note')}: ${receipt.note}` }}</p>
          <v-list
            class="list-receipt-summary pa-1 ma-4 super-dense"
            style="border: 1px solid rgba(0, 0, 0, .12)"
          >
            <v-list-item>
              <v-list-item-content>{{ $t('Subtotal') }}</v-list-item-content>
              <v-list-item-content class="justify-end">
                <!-- {{ receipt.totalProductsInclVat + (-receipt.totalDiscountInclVat) || 0 | money }} -->
                {{ receipt.totalOriginalProductsInclVat || 0 | money }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="receipt.totalDiscountInclVat">
              <v-list-item-content>
                <template v-if="receipt.totalDiscountInclVat > 0">{{ $t('Addition') }}</template>
                <template v-else>{{ $t('Discount') }}</template>
              </v-list-item-content>
              <v-list-item-content class="justify-end">{{ receipt.roundedTotalDiscountInclVat | money }}</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>{{ $t('Total') }}</v-list-item-content>
              <v-list-item-content class="justify-end">{{ receipt.totalInclVat | money }}</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content class="font-weight-bold">
                <span>
                  {{ $t('Total') }}
                  <span class="font-weight-regular font-italic">{{ `(${$t('Rounded').toLowerCase()})` }}</span>
                </span>
              </v-list-item-content>
              <v-list-item-content class="justify-end">{{ receipt.totalInclVatRounded | money }}</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>{{ $t('VAT') }}</v-list-item-content>
              <v-list-item-content class="justify-end">{{ receipt.totalVat | money }}</v-list-item-content>
            </v-list-item>
            <v-divider class="pb-1" />
            <v-list-item
              v-for="transaction in receipt.transactions"
              :key="transaction.uuid"
            >
              <v-list-item-content>
                {{ transaction.isRefund ? $t('Refund') : _.get(transaction, 'paymentMethod.name') }}
              </v-list-item-content>
              <v-list-item-content class="justify-end">{{ transaction.totalInclVat | money }}</v-list-item-content>
            </v-list-item>
          </v-list>
        </v-col>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <template v-if="!isParkedReceipt && _.get(receipt, 'customer.allowInfoEmails')">
          <v-btn
            :loading="$wait.is('sending email')"
            class="ml-0"
            color="black"
            dark
            depressed
            text
            @click="sendEmailConfirmation"
          >
            <v-icon
              dark
              small
              left
            >fal fa-envelope</v-icon>
            {{ $t('Send {0}', [$t('E-mail')]) }}
          </v-btn>
        </template>
        <template v-if="isParkedReceipt">
          <v-btn
            :loading="$wait.is(`canceling receipt ${receipt.uuid}`)"
            class="ml-0"
            color="red"
            dark
            depressed
            text
            @click="$emit('canceled', receipt.uuid)"
          >
            <v-icon
              dark
              small
              left
            >fal fa-trash-alt</v-icon>
            {{ $t('Delete {0}', [$t('Receipt')]) }}
          </v-btn>
        </template>
        <v-spacer />
        <receipt-printer
          v-if="showPrintButton"
          :receipt-object="receipt"
        >
          <template v-slot:activator>
            <v-btn
              :loading="$wait.is('printing receipt')"
              class="ml-0"
              color="primary"
              dark
              depressed
              text
            >
              <v-icon
                dark
                left
              >print</v-icon>
              {{ $t('Print') }}
            </v-btn>
          </template>
        </receipt-printer>
        <template v-if="isParkedReceipt">
          <v-btn
            :loading="$wait.is(`importing receipt ${receipt.uuid}`)"
            class="ml-0"
            color="primary"
            dark
            depressed
            text
            @click="$emit('imported', receipt.uuid)"
          >
            <v-icon
              dark
              left
              small
            >fal fa-external-link</v-icon>
            {{ $t('Load {0}', [$t('Receipt')]) }}
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
    <select-customer-dialog
      :show-dialog="showCustomersDialog"
      @closed="showCustomersDialog = false"
      @item-saved="onCustomerSelect($event.uuid)"
      @item-selected="onCustomerSelect($event.uuid)"
    />
  </div>
</template>

<script>
import StatusChip from '@/components/chips/StatusChip'
import ReceiptPrinter from '@/components/receipts/ReceiptPrinter'
import { RECEIPT_PENDING } from '@/utils/statuses/receipt'
import receiptService from '@/services/receipts'
import Flag from '@/components/Flag'
import SelectCustomerDialog from '@/components/dialogs/SelectCustomerDialog'
import camelcaseKeys from 'camelcase-keys'

export default {
  name: 'ReceiptInfo',

  components: {
    StatusChip,
    ReceiptPrinter,
    Flag,
    SelectCustomerDialog
  },

  props: {
    receiptObject: {
      default: null,
      type: Object
    },
    showPrintButton: {
      type: Boolean,
      default: true,
      required: false
    }
  },

  data () {
    return {
      showCustomersDialog: false,
      RECEIPT_PENDING,
      receipt: null
    }
  },

  computed: {
    isParkedReceipt () {
      return this.receipt.parkedName && this.receipt.status === RECEIPT_PENDING
    }
  },

  watch: {
    receiptObject: {
      immediate: true,
      handler (receiptObject) {
        this.receipt = Object.assign({}, receiptObject)
      }
    }
  },

  methods: {
    sendEmailConfirmation () {
      this.$wait.start('sending email')
      receiptService.sendEmailConfirmation(this.receipt.uuid)
        .then((data) => this.helpers.toastSuccess(data.data.message))
        .catch(data => { if (data && data.data.message) this.helpers.toastError(data.data.message) })
        .finally((data) => this.$wait.end('sending email'))
    },
    onCustomerSelect (data) {
      this.showCustomersDialog = false
      // save customer_uuid to receipt
      return this.$store.dispatch('RECEIPT/CHANGE_CUSTOMER', { receiptUuid: this.receipt.uuid, customerUuid: data })
        .then(data => {
          // Reload current ReceiptInfo component
          receiptService.get(this.receipt.uuid)
            .then(data => {
              this.receipt = camelcaseKeys(data.data.receipt, { deep: true })
            })

          this.$emit('customer-selected')
          this.helpers.toastSuccess(data.message)
        })
        .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
    }
  }
}
</script>

<style lang="scss" scoped>
  .list-receipt-summary /deep/ .v-list__item {
    height: 30px !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/receipts/ReceiptInfo.vue